/**
 * Display helpers shared by the file dialogs. Pure functions — no React.
 */
import {
  Archive,
  File as FileIcon,
  FileCode,
  FileSpreadsheet,
  FileText,
  Film,
  Image as ImageIcon,
  Music,
  Presentation,
  type LucideIcon,
} from 'lucide-react';
import { mimeToFileKind, type VaultFileKind } from '@/lib/vault-file-kinds';

export function formatBytes(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes < 0) return '—';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
  return `${(bytes / 1024 / 1024 / 1024).toFixed(1)} GB`;
}

/** "Today 14:05", "Yesterday", "12 Mar", "12 Mar 2024" — the modified column. */
export function formatModified(iso: string, now: Date = new Date()): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '—';
  const sameDay = (a: Date, b: Date) =>
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate();
  if (sameDay(date, now)) {
    return `Today ${date.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })}`;
  }
  const yesterday = new Date(now);
  yesterday.setDate(now.getDate() - 1);
  if (sameDay(date, yesterday)) return 'Yesterday';
  return date.toLocaleDateString(undefined, {
    day: 'numeric',
    month: 'short',
    ...(date.getFullYear() === now.getFullYear() ? {} : { year: 'numeric' }),
  });
}

const KIND_ICONS: Record<VaultFileKind, LucideIcon> = {
  image: ImageIcon,
  pdf: FileText,
  document: FileText,
  spreadsheet: FileSpreadsheet,
  presentation: Presentation,
  audio: Music,
  video: Film,
  archive: Archive,
  code: FileCode,
  other: FileIcon,
};

export function iconForMime(mimeType: string | null | undefined): LucideIcon {
  return KIND_ICONS[mimeToFileKind(mimeType)];
}

/** Short type label for the Type column ("PDF", "PNG image", "Spreadsheet"). */
export function typeLabel(name: string, mimeType: string | null | undefined): string {
  const kind = mimeToFileKind(mimeType);
  const ext = extensionOf(name).toUpperCase();
  switch (kind) {
    case 'pdf':
      return 'PDF';
    case 'image':
      return ext ? `${ext} image` : 'Image';
    case 'audio':
      return ext ? `${ext} audio` : 'Audio';
    case 'video':
      return ext ? `${ext} video` : 'Video';
    case 'spreadsheet':
      return 'Spreadsheet';
    case 'presentation':
      return 'Presentation';
    case 'document':
      return ext ? `${ext} document` : 'Document';
    case 'archive':
      return 'Archive';
    case 'code':
      return ext ? `${ext} file` : 'Code';
    default:
      return ext ? `${ext} file` : 'File';
  }
}

/** The extension of a file name, without the dot ("" when there is none). A
 *  leading dot is a hidden-file name, not an extension. */
export function extensionOf(name: string): string {
  const dot = name.lastIndexOf('.');
  return dot > 0 && dot < name.length - 1 ? name.slice(dot + 1) : '';
}

/** Split a name into base and extension (".pdf" included), for the Save
 *  dialog's protected extension. */
export function splitName(name: string): { base: string; extension: string } {
  const ext = extensionOf(name);
  return ext
    ? { base: name.slice(0, -(ext.length + 1)), extension: `.${ext}` }
    : { base: name, extension: '' };
}
